import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { isEmpty } from 'tools/string'
import Flex from 'components/Flex'
import { H4 } from 'components/Typo'
import IconMinus from 'components/Icons/IconMinus'
import IconPlus from 'components/Icons/IconPlus'
import Button from 'components/Button'
import NumberInput from 'components/NumberInput'
import { paddingSizes, buttonStyles, buttonShapes } from 'theme/default'

const StyledButton = styled(Button).attrs(() => ({
  buttonStyle: buttonStyles.outline,
  buttonShape: buttonShapes.round
}))`
  height: 40px;
  width: 40px;
  padding: 0;
`

const StyledNumberInput = styled(NumberInput)`
  text-align: center;
  height: 40px;
  width: 42px;
  box-sizing: border-box;
`

class QuantitySelector extends React.PureComponent {
  static propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    onChange: PropTypes.func,
    onInputBlur: PropTypes.func,
    onButtonClick: PropTypes.func,
    controlled: PropTypes.bool,
    disabled: PropTypes.bool,
    disableIncrease: PropTypes.bool,
    withInput: PropTypes.bool
  }

  static defaultProps = {
    label: 'input',
    value: 0,
    min: 0,
    max: 9999,
    step: 1,
    onButtonClick: () => {},
    onChange: () => {},
    onInputBlur: () => {},
    controlled: false,
    disabled: false,
    withInput: false
  }

  static getDerivedStateFromProps (nextProps) {
    if (nextProps.controlled && nextProps.value !== undefined) {
      return {
        value: nextProps.value || 0
      }
    }
    return null
  }

  constructor (props) {
    super(props)
    this.state = {
      value: props.value || props.min || 0
    }
  }

  updateValue = (value) => {
    const { controlled, name, onChange } = this.props
    if (controlled) {
      onChange(name, value)
    } else {
      this.setState({ value }, onChange.bind(null, name, value))
    }
  }

  decrease = () => {
    const { name, min, step, onButtonClick } = this.props
    const { value } = this.state
    const nextValue = value - step >= min ? value - step : value
    this.updateValue(nextValue)

    if (typeof onButtonClick === 'function') {
      onButtonClick(name, nextValue)
    }
  }

  increase = () => {
    const { name, max, min, step, disableIncrease, onButtonClick } = this.props
    if (disableIncrease) return
    const { value } = this.state
    const nextValue = (min === undefined || value + step <= min)
      ? min
      : (max === undefined || value + step <= max)
        ? value + step
        : value
    this.updateValue(nextValue)

    if (typeof onButtonClick === 'function') {
      onButtonClick(name, nextValue)
    }
  }

  onInputChange = (_, value) => {
    const { max } = this.props

    const nextValue = parseInt(isEmpty(value) ? '0' : value)

    if (nextValue > max) return

    this.updateValue(nextValue)
  }

  render () {
    const { name, label, className, min, disabled, disableIncrease, withInput, onInputBlur } = this.props
    const { value } = this.state
    const disableDecrease = value <= min
    return (
      <Flex className={className} alignItems='center'>
        <StyledButton data-testid='button.decrease' onClick={this.decrease} disabled={disabled || disableDecrease}>
          {() => (<IconMinus iconSize='medium' fill='currentColor' />)}
        </StyledButton>
        {withInput ? (
          <Flex padding={paddingSizes.small} height={40} width={65} justifyContent='center' alignItems='center'>
            <StyledNumberInput
              controlled
              data-testid='quantitySelector.numberInput'
              name={name}
              label={label}
              value={value}
              disabled={disabled}
              onBlur={onInputBlur}
              onChange={this.onInputChange}
            />
          </Flex>
        ) : (
          <Flex height={40} width={40} justifyContent='center' alignItems='center'>
            <H4 data-testid='value' bold>{value}</H4>
          </Flex>
        )}
        <StyledButton data-testid='button.increase' onClick={this.increase} disabled={disabled || disableIncrease}>
          {() => (<IconPlus iconSize='medium' fill='currentColor' />)}
        </StyledButton>
      </Flex>
    )
  }
}

export default QuantitySelector
